import { toast } from "react-toastify";
import { useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppleIcon from "@mui/icons-material/Apple";
import {
  getAuth,
  OAuthProvider,
  UserCredential,
  GoogleAuthProvider,
  signInWithCredential,
  getAdditionalUserInfo,
} from "firebase/auth";
import { useDialog } from "app/hook";
import { AppDispatch } from "app/store";
import { parentsFirebase } from "app/Auth";
import { DialogType, AppPlatform } from "app/types";
import { actions as configActions } from "app/configSlice";
import Constants from "common/constant";
import { useScale, usePlaySound } from "common/utils";
import parentI18n from "features/parent/i18n";
import { updateLastSignInTime } from "features/user/api";
import {
  waitForGoogleSdk,
  waitForAppleIDSdk,
  isGoogleSdkLoaded,
  isAppleIDSdkLoaded,
  isNativeAppleSignInSupported,
} from "features/auth/utils";
import { useSignUp } from "./hook";
import { GoogleSignButton } from "./GoogleSignButton";
import {
  RegisterMethod,
  registerErrorCode,
  recordRegisterStart,
  recordRegisterError,
} from "./registerAnalytics";

export const SSO = ({
  signIn,
  onSuccess,
  keepLoadingOnSuccess = false,
}: {
  signIn: boolean;
  onSuccess?: () => void;
  keepLoadingOnSuccess?: boolean;
}) => {
  useSignUp();
  const play = usePlaySound();
  const { width } = useScale();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const auth = getAuth(parentsFirebase);
  const buttonDivRef = useRef<HTMLDivElement>(null);
  // 登録（signIn でないとき）で使った方法。失敗の記録に付ける
  const registerMethodRef = useRef<RegisterMethod | null>(null);

  const startRegister = (method: RegisterMethod) => {
    if (signIn) return;
    registerMethodRef.current = method;
    recordRegisterStart(method);
  };
  const nonce = Math.random().toString(36).substring(2, 10);

  const handleSignSuccess = (userCredential: UserCredential) => {
    updateLastSignInTime();
    dispatch(configActions.setJustAuthenticated(true));
    // ダイアログ版（keepLoadingOnSuccess）は認証成功後もダイアログが閉じる
    // まで AuthProvider がローディングを担うため、ここでは解除しない。
    if (!keepLoadingOnSuccess) {
      handleDialog({ type: DialogType.LOADING, value: false });
    }
    const additionalUserInfo = getAdditionalUserInfo(userCredential);
    if (additionalUserInfo.isNewUser) {
      toast(
        <div>
          <p className="text text-textcolor-black">{parentI18n.t("MSG_SIGN_UP_SUCCESS_CONTENT")}</p>
        </div>,
      );
    } else {
      toast(
        <div>
          <p className="text text-textcolor-black">{parentI18n.t("MSG_SIGN_IN_SUCCESS_CONTENT")}</p>
        </div>,
      );
    }
    if (onSuccess) {
      onSuccess();
    } else {
      navigate("/");
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleError = (error: any) => {
    if (!signIn) {
      recordRegisterError(registerMethodRef.current, registerErrorCode(error));
    }
    handleDialog({ type: DialogType.LOADING, value: false });
    console.error(`sign error: ${JSON.stringify(error)}`);
  };

  const handleFirebaseGoogleSignIn = async (idToken: string) => {
    const credential = GoogleAuthProvider.credential(idToken);
    signInWithCredential(auth, credential).then(handleSignSuccess).catch(handleError);
  };

  // Web の Google のボタンは SDK が描くので、押したことは分からない。認証情報が届いたところを始めとする
  const handleGoogleCredentialResponse = (response) => {
    startRegister(RegisterMethod.GOOGLE);
    handleFirebaseGoogleSignIn(response.credential);
  };

  // rawNonce はネイティブが Apple へ nonce を渡したときだけ送られてくる（Android）。
  // iOS と Web は従来どおりこちらで作った nonce を使う
  const handleFirebaseAppleSignIn = async (idToken: string, rawNonce?: string) => {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    const credential = provider.credential({
      idToken: idToken,
      rawNonce: rawNonce ?? nonce,
    });
    signInWithCredential(auth, credential).then(handleSignSuccess).catch(handleError);
  };

  const handleWebAppleSignInSuccess = (event) => {
    console.warn(`web apple sign in success: ${JSON.stringify(event)}`);
    startRegister(RegisterMethod.APPLE);
    handleFirebaseAppleSignIn(event.detail.authorization.id_token);
  };

  const handleWebAppleSignInFailure = (event) => {
    handleDialog({ type: DialogType.LOADING, value: false });
    console.error(`web apple sign in failure: ${JSON.stringify(event)}`);
  };

  const handleMessage = (message) => {
    let parsed;
    try {
      parsed = JSON.parse(message.data);
    } catch {
      // アプリ本体以外から届く JSON でないメッセージは無視する
      // （iOS では Firebase Auth の iframe が `!_{...}` の形のメッセージを送ってくる）
      return;
    }
    const { command, data } = parsed;
    switch (command) {
      case "signIn": {
        const { method } = data;
        switch (method) {
          case "apple":
            if (data.idToken) {
              handleFirebaseAppleSignIn(data.idToken, data.rawNonce);
            } else {
              handleError(data.result);
            }
            break;
          case "google":
            if (data.idToken) {
              handleFirebaseGoogleSignIn(data.idToken);
            } else {
              handleError(data.result);
            }
            break;
          default:
            break;
        }
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    const init_apple_sign = async () => {
      const encoder = new TextEncoder();
      const encodedNonce = encoder.encode(nonce);
      const hashBuffer = await crypto.subtle.digest("SHA-256", encodedNonce);
      const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
      const hashedNonce = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

      await waitForAppleIDSdk();
      if (!isAppleIDSdkLoaded()) return;

      // @ts-expect-error AppleID is provided by the Apple SDK loaded at runtime
      AppleID.auth.init({
        clientId: "com.inexus.app.services.parents",
        scope: "name email",
        redirectURI: Constants.publicUrl,
        state: "kids",
        nonce: hashedNonce,
        usePopup: true,
      });
    };

    if ("ReactNativeWebView" in window) {
      if (window.kidsAppPlatform === "ios") {
        window.addEventListener("message", handleMessage);
      } else {
        document.addEventListener("message", handleMessage);
      }
    } else {
      init_apple_sign();

      document.addEventListener("AppleIDSignInOnSuccess", handleWebAppleSignInSuccess);

      document.addEventListener("AppleIDSignInOnFailure", handleWebAppleSignInFailure);
    }

    return () => {
      if ("ReactNativeWebView" in window) {
        if (window.kidsAppPlatform === "ios") {
          window.removeEventListener("message", handleMessage);
        } else {
          document.removeEventListener("message", handleMessage);
        }
      } else {
        document.removeEventListener("AppleIDSignInOnSuccess", handleWebAppleSignInSuccess);

        document.removeEventListener("AppleIDSignInOnFailure", handleWebAppleSignInFailure);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window.ReactNativeWebView) return;

    let cancelled = false;
    const initGoogleSign = async () => {
      await waitForGoogleSdk();
      if (cancelled || !isGoogleSdkLoaded()) return;

      // @ts-expect-error google is provided by the Google Identity SDK loaded at runtime
      google.accounts.id.initialize({
        client_id: Constants.googleSignClientId,
        callback: handleGoogleCredentialResponse,
      });
      if (buttonDivRef.current) {
        const rect = buttonDivRef.current.getBoundingClientRect();

        // @ts-expect-error google is provided by the Google Identity SDK loaded at runtime
        google.accounts.id.renderButton(buttonDivRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          width: Math.max(rect.width, 375),
          logo_alignment: "left",
          locale: "ja",
          text: signIn ? "sign_in_with" : "sign_up_with",
        });
      }
    };

    initGoogleSign();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const handleAppleSign = async () => {
    play();
    if (window.ReactNativeWebView) {
      startRegister(RegisterMethod.APPLE);
      handleDialog({ type: DialogType.LOADING, value: true });
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          command: "signIn",
          data: {
            method: "apple",
          },
        }),
      );
    }
  };

  const handleGoogleSign = () => {
    play();
    if ("ReactNativeWebView" in window) {
      startRegister(RegisterMethod.GOOGLE);
      handleDialog({ type: DialogType.LOADING, value: true });
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          command: "signIn",
          data: {
            method: "google",
          },
        }),
      );
    }
  };

  return (
    <>
      {/* アプリ内の自前ボタン。Android は実装を載せた版（buildVersion 98 以上）
          でだけ出す。ブラウザは次の分岐で Apple 公式ボタンへ回る */}
      {isNativeAppleSignInSupported() ? (
        <div
          onClick={handleAppleSign}
          className="flex-row-el flex-center w-[375px] h-[40px] bg-[#000] rounded-lg cursor-pointer"
        >
          <AppleIcon sx={{ fontSize: 16, marginRight: 0.5, color: "white" }} />
          <p className="text-body text-[16px] text-white">
            {signIn
              ? parentI18n.t("MSG_SIGN_IN_WITH_APPLE")
              : parentI18n.t("MSG_SIGN_UP_WITH_APPLE")}
          </p>
        </div>
      ) : !window.ReactNativeWebView ? (
        <div
          className="w-[375px] h-[40px] cursor-pointer flex-col-el flex-center"
          id="appleid-signin"
          data-color="black"
          data-border="true"
          data-mode="center-align"
          data-logo-size="large"
          data-type="continue"
        />
      ) : null}

      {window.ReactNativeWebView ? (
        <div
          onClick={handleGoogleSign}
          className="flex-row-el flex-center w-[375px] h-[42px] bg-[#fff] rounded-md border-solid border-[1px] border-[#dadce0] cursor-pointer"
        >
          <GoogleSignButton />
          <p className="text-body text-[16px] text-textcolor-black">
            {parentI18n.t("MSG_SIGN_IN_WITH_GOOGLE")}
          </p>
        </div>
      ) : (
        <div ref={buttonDivRef} className="max-w-[375px] w-full h-[44px]">
          <div id="buttonDiv"></div>
        </div>
      )}

      {/* Amazon（Fire OS）の子ども用プロフィールでは外部ブラウザからの戻りが制限され
          Googleログインが完了できないことがあるため、注記を表示する */}
      {window.ReactNativeWebView && window.kidsAppPlatform === AppPlatform.Amazon && (
        <p className="type-text type-sm text-gray2-100 w-[375px] -mt-4">
          {parentI18n.t("MSG_SIGN_GOOGLE_AMAZON_NOTICE")}
        </p>
      )}
    </>
  );
};
